import {useDataProvider, useNotify, useQuery, useRefresh} from "react-admin";
import React, {useState} from "react";
import {Button, Divider, Form, Modal, Space, Switch, Typography} from "antd";
import {EditOutlined, ThunderboltOutlined} from "@ant-design/icons";
import {createSubmitHandler} from "@src/components/Settings/utils";
import {ExtendedDataProvider} from "@src/types";
import MonacoEditorWrapper from "@src/components/Common/MonacoEditorWrapper";
import SchemaMonacoEditor from "@src/components/Services/SubComponents/SchemaMonacoEditor";
import {GEN_UI_CATALOG_SCHEMA} from "@src/ui/GenUIRenderer";

const {Title} = Typography;

const jsonSchemaFields = [
    'universal_executor_config_schema',
    'universal_executor_verify_schema',
    'universal_executor_execution_schema',
    'generative_ui_catalog_schema',
];

const getSchemaInitialValue = (config: any, field: string): string => {
    const val = config?.[field];
    if (!val) return '';
    if (typeof val === 'string') return val;
    return JSON.stringify(val, null, 2);
};

interface EditorModalProps {
    visible: boolean;
    title: string;
    language: 'plaintext' | 'json';
    value: string;
    onOk: (value: string) => void;
    onCancel: () => void;
    onAutoGenerate?: () => string;
}

const EditorModal: React.FC<EditorModalProps> = ({visible, title, language, value, onOk, onCancel, onAutoGenerate}) => {
    const [editorValue, setEditorValue] = useState(value);

    React.useEffect(() => {
        if (visible) setEditorValue(value);
    }, [visible, value]);

    return (
        <Modal
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
                    <span>{title}</span>
                    {onAutoGenerate && (
                        <Button
                            icon={<ThunderboltOutlined />}
                            onClick={() => setEditorValue(onAutoGenerate())}
                            size="small"
                        >
                            Auto Generate Catalog
                        </Button>
                    )}
                </div>
            }
            open={visible}
            onOk={() => onOk(editorValue)}
            onCancel={onCancel}
            width={900}
            destroyOnClose
        >
            {language === 'json'
                ? <SchemaMonacoEditor value={editorValue} onChange={(v) => setEditorValue(v || '')} height="500px" />
                : <MonacoEditorWrapper value={editorValue} onChange={(v) => setEditorValue(v || '')} />
            }
        </Modal>
    );
};

const AIIntegrationSettingsForm = () => {
    const notify = useNotify()
    const [form] = Form.useForm();
    const refresh = useRefresh();
    const dataProvider: ExtendedDataProvider = useDataProvider()
    const settingsName = "AIIntegration"

    const [modalState, setModalState] = useState<{
        visible: boolean;
        title: string;
        language: 'plaintext' | 'json';
        fieldName: string;
    }>({visible: false, title: '', language: 'plaintext', fieldName: ''});

    const {loaded, error, data: currentSettings} = useQuery({
        type: 'getOne',
        resource: 'general_settings',
        payload: {id: settingsName}
    });

    const handleFinish = (values: any) => {
        const processed = {...values};
        jsonSchemaFields.forEach(key => {
            if (typeof processed[key] === 'string' && processed[key].trim()) {
                try {
                    processed[key] = JSON.parse(processed[key]);
                } catch (e) {
                    // leave as string if not valid JSON
                }
            }
        });
        return createSubmitHandler(settingsName, currentSettings, dataProvider, notify, refresh)(processed);
    };

    const openModal = (fieldName: string, title: string, language: 'plaintext' | 'json') => {
        setModalState({visible: true, title, language, fieldName});
    };

    const handleModalOk = (value: string) => {
        form.setFieldsValue({[modalState.fieldName]: value});
        setModalState(prev => ({...prev, visible: false}));
    };

    const config = currentSettings?.['config'];

    return (
        <>
            {
                (loaded || error) &&
                <Form
                    form={form}
                    labelAlign='left'
                    labelCol={{ span: 10 }}
                    wrapperCol={{ span: 14 }}
                    onFinish={handleFinish}
                >
                    <Form.Item
                        label={"Show AI Validator"}
                        initialValue={config?.show_ai_validator || false}
                        name="show_ai_validator"
                        rules={[{required: true}]}
                    >
                        <Switch defaultChecked={config?.show_ai_validator == true}/>
                    </Form.Item>

                    <Form.Item
                        label={"Show AI Processor"}
                        initialValue={config?.show_ai_processor || false}
                        name="show_ai_processor"
                        rules={[{required: true}]}
                    >
                        <Switch defaultChecked={config?.show_ai_processor == true}/>
                    </Form.Item>

                    <Divider titlePlacement="start"><Title level={5}>Universal Executor</Title></Divider>

                    {/* Hidden fields to hold values */}
                    <Form.Item name="universal_executor_base_prompt" initialValue={config?.universal_executor_base_prompt || ''} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_config_prompt" initialValue={config?.universal_executor_config_prompt || ''} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_config_schema" initialValue={getSchemaInitialValue(config, 'universal_executor_config_schema')} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_verify_prompt" initialValue={config?.universal_executor_verify_prompt || ''} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_verify_schema" initialValue={getSchemaInitialValue(config, 'universal_executor_verify_schema')} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_execution_prompt" initialValue={config?.universal_executor_execution_prompt || ''} hidden><input /></Form.Item>
                    <Form.Item name="universal_executor_execution_schema" initialValue={getSchemaInitialValue(config, 'universal_executor_execution_schema')} hidden><input /></Form.Item>

                    {/* Base Prompt row */}
                    <Form.Item label="Base Prompt">
                        <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_base_prompt', 'Base Prompt', 'plaintext')}>
                            Edit Prompt
                        </Button>
                    </Form.Item>

                    {/* Config row */}
                    <Form.Item label="Config">
                        <Space>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_config_prompt', 'Config Prompt', 'plaintext')}>
                                Edit Prompt
                            </Button>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_config_schema', 'Config Response Schema', 'json')}>
                                Edit Schema
                            </Button>
                        </Space>
                    </Form.Item>

                    {/* Verify row */}
                    <Form.Item label="Verify">
                        <Space>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_verify_prompt', 'Verify Prompt', 'plaintext')}>
                                Edit Prompt
                            </Button>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_verify_schema', 'Verify Response Schema', 'json')}>
                                Edit Schema
                            </Button>
                        </Space>
                    </Form.Item>

                    {/* Execution row */}
                    <Form.Item label="Execution">
                        <Space>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_execution_prompt', 'Execution Prompt', 'plaintext')}>
                                Edit Prompt
                            </Button>
                            <Button icon={<EditOutlined />} onClick={() => openModal('universal_executor_execution_schema', 'Execution Response Schema', 'json')}>
                                Edit Schema
                            </Button>
                        </Space>
                    </Form.Item>

                    <Divider titlePlacement="start"><Title level={5}>Optimiser</Title></Divider>

                    <Form.Item name="optimiser_prompt" initialValue={config?.optimiser_prompt || ''} hidden><input /></Form.Item>

                    <Form.Item label="Optimiser Prompt">
                        <Button icon={<EditOutlined />} onClick={() => openModal('optimiser_prompt', 'Optimiser Prompt', 'plaintext')}>
                            Edit Prompt
                        </Button>
                    </Form.Item>

                    <Divider titlePlacement="start"><Title level={5}>Generative UI</Title></Divider>

                    <Form.Item name="generative_ui_catalog_schema" initialValue={getSchemaInitialValue(config, 'generative_ui_catalog_schema')} hidden><input /></Form.Item>

                    <Form.Item label="Generative UI Catalog Schema">
                        <Button icon={<EditOutlined />} onClick={() => openModal('generative_ui_catalog_schema', 'Generative UI Catalog Schema', 'json')}>
                            Edit Schema
                        </Button>
                    </Form.Item>

                </Form>
            }

            <EditorModal
                visible={modalState.visible}
                title={modalState.title}
                language={modalState.language}
                value={form.getFieldValue(modalState.fieldName) || ''}
                onOk={handleModalOk}
                onCancel={() => setModalState(prev => ({...prev, visible: false}))}
                onAutoGenerate={modalState.fieldName === 'generative_ui_catalog_schema'
                    ? () => JSON.stringify(GEN_UI_CATALOG_SCHEMA, null, 2)
                    : undefined
                }
            />
        </>
    )
}

export default AIIntegrationSettingsForm;
